import { type FormEvent, useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { Map, MapPin, Plus, RefreshCw, Search, Shapes } from "lucide-react"
import { smartoltService, type SmartOLTZones } from "../../../services/smartolt.service.ts"
import { toast } from "../../../utils/notify.ts"
import { SMARTOLT_PATHS } from "../smartoltNav.ts"

const empty: SmartOLTZones = {
  zones: [],
  summary: { total: 0, unassigned_onus: 0, total_onus: 0, total_odbs: 0 },
  updated_at: null,
}

function formatUpdated(value?: string | null) {
  if (!value) return "—"
  const dt = new Date(value)
  if (Number.isNaN(dt.getTime())) return value
  return dt.toLocaleString(undefined, { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" })
}

export function SmartOLTZonesPage() {
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [data, setData] = useState<SmartOLTZones>(empty)
  const [search, setSearch] = useState("")
  const [showAdd, setShowAdd] = useState(false)
  const [zoneName, setZoneName] = useState("")
  const [saving, setSaving] = useState(false)

  const load = async (silent = false, sync = false) => {
    if (!silent) setLoading(true)
    setRefreshing(true)
    try {
      if (sync) {
        try { await smartoltService.runSyncNow(25) } catch { /* list cached data */ }
      }
      const next = await smartoltService.getZones()
      setData({
        zones: Array.isArray(next?.zones) ? next.zones : [],
        summary: next?.summary || empty.summary,
        updated_at: next?.updated_at || null,
      })
    } catch {
      toast.error("Failed to load zones")
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return data.zones
    return data.zones.filter((row) =>
      [row.name, row.zone_id].filter(Boolean).join(" ").toLowerCase().includes(q),
    )
  }, [data.zones, search])

  const addZone = async (event: FormEvent) => {
    event.preventDefault()
    const zone = zoneName.trim()
    if (!zone) { toast.error("Zone name is required"); return }
    setSaving(true)
    try {
      await smartoltService.createCommand("add_zone", { form_data: { zone } })
      await smartoltService.runCommandsNow(5)
      toast.success("Zone add command sent")
      setZoneName("")
      setShowAdd(false)
      await load(true)
    } catch (error: any) {
      toast.error(error?.response?.data?.error || "Failed to add zone")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Zones</h1>
          <p className="mt-0.5 text-xs text-slate-500">Geographic groups of ONUs by town, neighborhood or village</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-lg bg-slate-100 border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700">
            {data.summary.total} zones
          </span>
          <span className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-1.5 text-xs font-semibold text-blue-700">
            {data.summary.total_onus} ONUs
          </span>
          <button type="button" onClick={() => setShowAdd((v) => !v)}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 shadow-xs transition-colors hover:bg-slate-50 hover:text-blue-600">
            <Plus className="h-3.5 w-3.5" />
            Add Zone
          </button>
          <Link to={SMARTOLT_PATHS.maps}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 px-3 text-sm font-medium text-white shadow-xs transition-colors">
            <Map className="h-3.5 w-3.5" />
            Open Map
          </Link>
        </div>
      </div>

      {/* Add zone form */}
      {showAdd && (
        <form onSubmit={addZone} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-xs">
          <p className="mb-3 text-sm font-semibold text-slate-800">Add New Zone</p>
          <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr),auto]">
            <label className="text-xs font-medium text-slate-600">
              Zone name
              <input
                value={zoneName}
                onChange={(e) => setZoneName(e.target.value)}
                placeholder="e.g. New Town, Sector 5"
                className="mt-1 h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 placeholder-slate-400 font-normal focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/20"
              />
            </label>
            <button type="submit" disabled={saving}
              className="self-end inline-flex h-9 items-center justify-center rounded-lg bg-blue-600 hover:bg-blue-700 px-5 text-sm font-medium text-white shadow-xs disabled:opacity-50 transition-colors">
              {saving ? "Adding…" : "Add"}
            </button>
          </div>
        </form>
      )}

      {/* Info banner */}
      <div className="rounded-xl border border-blue-200 bg-blue-50/50 px-4 py-3 text-xs text-blue-900">
        <p className="font-semibold text-blue-900">About zones</p>
        <p className="mt-1 text-slate-600">
          Zones sync from SmartOLT and group ONUs for maps and filters. They are display-only and do not change ONU settings.
          View ONU locations on the{" "}
          <Link to={SMARTOLT_PATHS.maps} className="font-semibold text-blue-600 hover:underline">Network Map</Link>.
        </p>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-xs">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50/50 p-3">
          <div className="relative min-w-[180px] flex-1 sm:max-w-xs">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search zones…"
              className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-8 pr-3 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/20"
            />
          </div>
          <button type="button" disabled={refreshing} onClick={() => load(true, true)}
            className="ml-auto inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 shadow-xs transition-colors hover:bg-slate-50 hover:text-blue-600 disabled:opacity-50">
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
            Sync from SmartOLT
          </button>
        </div>

        {loading ? (
          <div className="px-4 py-12 text-center">
            <RefreshCw className="mx-auto h-7 w-7 animate-spin text-blue-500 mb-2" />
            <p className="text-slate-500 text-sm">Loading zones…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-14 text-center">
            <Shapes className="mx-auto h-12 w-12 text-slate-300 mb-3" />
            <p className="text-sm font-semibold text-slate-800">No zones yet</p>
            <p className="mt-1 text-xs text-slate-500 max-w-xs mx-auto">
              Zones appear after SmartOLT sync. Check the map for ONU GPS coverage or run sync from system settings.
            </p>
            <Link to={SMARTOLT_PATHS.maps}
              className="mt-5 inline-flex h-9 items-center gap-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 px-4 text-sm font-medium text-white shadow-xs transition-colors">
              <Map className="h-3.5 w-3.5" />
              Open Map
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                  {["Zone", "Zone ID", "ONUs", "ODBs", "Synced"].map((h, i) => (
                    <th key={h} className={`px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-600 ${i >= 2 ? "text-right" : "text-left"}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((row) => (
                  <tr key={row.zone_id} className="transition-colors hover:bg-slate-50">
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50">
                          <MapPin className="h-3.5 w-3.5 text-blue-600" />
                        </div>
                        <span className="font-medium text-slate-800">{row.name}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 font-mono text-xs text-slate-500">{row.zone_id}</td>
                    <td className="px-3 py-2.5 text-right">
                      <Link to={`${SMARTOLT_PATHS.configured}?zone=${encodeURIComponent(row.name)}`}
                        className="font-semibold text-blue-600 hover:text-blue-800 hover:underline">
                        {row.onus}
                      </Link>
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <Link to={SMARTOLT_PATHS.odbs} className="font-semibold text-blue-600 hover:text-blue-800 hover:underline">
                        {row.odbs}
                      </Link>
                    </td>
                    <td className="px-3 py-2.5 font-mono text-xs text-slate-500">{formatUpdated(row.fetched_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!loading && data.summary.unassigned_onus > 0 && (
        <p className="text-xs text-slate-500">
          <span className="font-semibold text-amber-600">{data.summary.unassigned_onus}</span> of {data.summary.total_onus} ONUs have no zone assigned.
        </p>
      )}
    </div>
  )
}
